import {
  FilterOptionKeys,
  GoalFilterDocument,
  GroupByMode,
  OrderDirection,
} from '@griply/types';
import {
  getGoalListFilters,
  getGoalListGroupOptions,
  getGoalListSortOptions,
  goalSortingModeTranslationKeys,
} from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDeadline16 } from 'shared/assets/icons';
import { FilterButton } from 'shared/components/ui/filter-button';
import { useUser } from 'shared/hooks/use-user';

export type GoalListFilterButtonProps = {
  filterDocument: GoalFilterDocument;
  defaultFilterDocument?: GoalFilterDocument;
  hideLifeArea?: boolean;
  onUpdate: (partial: Partial<GoalFilterDocument>) => void;
  onReset: () => void;
};

export const GoalListFilterButton: React.FC<GoalListFilterButtonProps> = ({
  filterDocument,
  defaultFilterDocument,
  hideLifeArea,
  onUpdate,
  onReset,
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const tags = useSortedTags();

  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;
  const handleSortDirectionChange = (direction: OrderDirection) =>
    updateUserSettings({ goalSortDirection: direction });

  // The goal "Deadline" group reuses the task group-by key labelled "Date".
  const labelOverrides = useMemo(
    () => ({
      [GroupByMode.Deadline]: t('general.labels.goals.sort.options.deadline'),
    }),
    [t],
  );
  // The goal "Date" filter targets a goal's start date.
  const filterLabelOverrides = useMemo(
    () => ({ [FilterOptionKeys.Date]: t('general.labels.start-date') }),
    [t],
  );
  const filterIconOverrides = useMemo(
    () => ({ [FilterOptionKeys.Deadline]: CalendarDeadline16 }),
    [],
  );

  return (
    <FilterButton
      filterDocument={filterDocument}
      defaultFilterDocument={defaultFilterDocument}
      goals={goals}
      lifeAreas={lifeAreas}
      tags={tags}
      updateFilterDocument={onUpdate}
      supportedGroupByModes={getGoalListGroupOptions({ hideLifeArea })}
      supportedSortingModes={getGoalListSortOptions({ hideLifeArea })}
      supportedFilters={getGoalListFilters({ hideLifeArea })}
      sortingTranslationKeys={goalSortingModeTranslationKeys}
      sortDirection={sortDirection}
      onSortDirectionChange={handleSortDirectionChange}
      labelOverrides={labelOverrides}
      filterLabelOverrides={filterLabelOverrides}
      filterIconOverrides={filterIconOverrides}
      title={t('context-menus.filtering.goal.title')}
      onReset={onReset}
      preventDrag
      renderAsIconButton
    />
  );
};
